/**
 * `richPhrase` is authored as Markdown in Contentful, which buys authors nested example lists for
 * free. On top of Markdown there is exactly one custom token:
 *
 *     ==text==  ->  <mark class="verbatim">text</mark>
 *
 * It marks the substring an expert must say word-for-word, and `mark.verbatim` is what
 * RichPhrase.module.css styles.
 *
 * The token is deliberately the *only* markup an author can emit. markdown-to-jsx parses inline
 * HTML by default, so `<` is escaped before the token is expanded — a stray or hostile tag typed
 * into the CMS renders as literal text instead of live markup. Escaping happens first so the
 * `<mark>` we inject afterwards is the one tag that survives.
 */
const VERBATIM_TOKEN_PATTERN = String.raw`==([^\n=]+)==`;
const VERBATIM_TOKEN = new RegExp(VERBATIM_TOKEN_PATTERN, "g");
// Non-global on purpose: `.test()` on a /g regex advances lastIndex between calls.
const HAS_VERBATIM_TOKEN = new RegExp(VERBATIM_TOKEN_PATTERN);

/**
 * Whether `text` contains a token `expandRichPhrase` would expand.
 *
 * Callers on surfaces shared with every partner (the sale-success view, the expert timeline, the
 * send-link ask) use this to leave untouched copy on its original plain-string path, so introducing
 * Markdown rendering for the pilot cannot change how anyone else's copy is displayed. Built from the
 * same pattern as the expansion so the two cannot disagree about what counts as a token.
 */
export function hasVerbatimToken(text: string): boolean {
    return HAS_VERBATIM_TOKEN.test(text);
}

/**
 * The plain-text form of a `richPhrase`, for surfaces that render a bare string and cannot show the
 * markup — the customer timeline's SalesGuidance card and the Honeycomb checklist payload. Drops the
 * `==` markers but keeps the words, unwraps list bullets, and collapses paragraph breaks to single
 * newlines, so those consumers get the scripted sentence rather than either markup or the terser
 * `phrase` standing in for it.
 */
export function richPhraseToPlainText(richPhrase: string): string {
    return richPhrase
        .replace(VERBATIM_TOKEN, "$1")
        .replace(/^[ \t]*-[ \t]+/gm, "")
        .replace(/\n{2,}/g, "\n")
        .trim();
}

export function expandRichPhrase(markdown: string): string {
    return markdown.replaceAll("<", "&lt;").replace(VERBATIM_TOKEN, '<mark class="verbatim">$1</mark>');
}
